import { MDBBtn, MDBCard, MDBCardBody, MDBCardTitle, MDBCheckbox, MDBChip, MDBCol, MDBDateTimepicker, MDBDatepicker, MDBFile, MDBIcon, MDBInput, MDBInputGroup, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBModalTitle, MDBRadio, MDBRow, MDBSelect, MDBSpinner, MDBTextArea, MDBTypography } from "mdb-react-ui-kit";
import { useParams, useNavigate, Link  } from 'react-router-dom';
import { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { generateClient, get } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';
import deepUpdateObject from "../../utils/deepUpdateObject";
import LectureRegistration from "../../components-user/event-management/lecture-registration/Index";
import { captureError } from "../../utils/sentryHelpers";
import lounges26LectureConfig from "./lectureEventConfig";
import { UserContext } from "../../App";
import { isHDLectureManager, hasHDIndustry } from "../../config/hdLectureManager";
import SlugField from "../../components/toolbox/SlugField";
import { isAdminOrModerator } from "../../utils/roles";
import { slugMutationInput } from "../../utils/slugInput";
import { pickSourceText } from "../../utils/slugify";


export default function AdminEditLecture() {
  let navigate = useNavigate();
  const client = generateClient();
  const { companyid, lectureid } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);
  // Public URL slug: Admin/Moderator only (not HDLectureManager); AppSync field auth rejects other inputs containing "slug"
  const canEditSlug = isAdminOrModerator(user);

  // States
  const [lecture, setLecture] = useState({});
  // Slug as loaded with the lecture; "slug" is only sent when it was changed here (see slugMutationInput)
  const [loadedSlug, setLoadedSlug] = useState(null);
  const [company, setCompany] = useState({});
  const [topics, setTopics] = useState(null);
  const [topicGroups, setTopicGroups] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [showLectureForm, setShowLectureForm] = useState(false);

  // Ref to access LectureRegistration's save function
  const lectureRegistrationRef = useRef();

  // Get lecture and company data
  const getLectureData = async () => {
    try {
      const lecture = await client.graphql({
        variables: { id: lectureid },
        query: /* GraphQL */ `
        query GetLecture($id: ID!) {
          getLecture(id: $id) {
            id
            type
            startTime
            endTime
            dateTimes
            event {
              id
              name {
                text
                language
              }
              status
              date
              startDate
              lectureDocumentsEndDate
              industries {
                items {
                  industry {
                    id
                  }
                }
              }
            }
            company {
              id
              name
              image {
                main {
                  fileName
                  alt
                }
              }
              industry
              status
              website
              admins
              
              x4AdminId
              companyAdminId
              adminEmployees {
                items {
                  status
                  image {
                    fileName
                    alt
                  }
                  user {
                    id
                    lastName
                    firstName
                    title
                    contact {
                      email
                      phone
                    }
                  }
                }
              }
          
            }
            speakers {
              items {
                id
                employeeId
              }
            }
            session
            mainSpeaker {
              id
              role
              permissions {
                resource
                actions
              }
              status
              
              WPJetEngineId
              permissions {
                resource
                actions
              }
              companyEmployeesId
              userEmployeeProfilesId
              
            }
            room
            slug
            slugHistory
            title {
              language
              text
              
            }
            subjects {
              id
              name {
                language
                text
              }
              
            }
            topics {
              items {
                topicId
              }
            }
            content {
              language
              text
              
            }
            conditions {
              name
              accepted
              
            }
            presentationLanguage
            image {
              fileName
              alt
              x4UUID
              
            }
            pdfDe {
              url
              fileName
              s3Path
              
            }
            pdfEn {
              url
              fileName
              s3Path
              
            }
            videoS3Key {
              s3Path
            }
            approvedInsight
            createdAt
            updatedAt
            companyLecturesId
            companyBooth
            eventLecturesId
            lectureMainSpeakerId
            
            adminComment
            adminTopic {
              id
            }
            approved
            lengthInSeconds
            status
          }
        }
      `,
      });
      return {
        ...lecture.data.getLecture,
        speakers: lecture.data.getLecture.speakers?.items?.map(speaker => speaker.employeeId) || [],
        speakersRaw: lecture.data.getLecture.speakers?.items || [],
        topics: lecture.data.getLecture.topics?.items?.map(topic => topic.topicId) || [],
        tempSubjects: lecture.data.getLecture.subjects?.map(subject => ({de: subject.name.find(name => name.language === "de").text, en: subject.name.find(name => name.language === "en").text})) || [],
        adminTopic: lecture.data.getLecture.adminTopic?.id || null,
      };
    } catch (error) {
      console.warn("Error getting lecture data", error);
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-lecture-data',
        context: { lectureId: lectureid },
        tags: { component: 'AdminEditLecture' }
      });
    }
  };

  // Specific topic group IDs to filter for LOUNGES events
  const ALLOWED_TOPIC_GROUP_IDS = [
    'b1f2ca84-0f9a-4d08-9b26-fd652a3ac5fc',
    '93ffdc86-e278-4a68-88b8-2d81a28cde65'
  ];

  // Get topics
  const getTopics = async () => {
    try {
      const topicGroupsWithTopics = await client.graphql({
        query: /* GraphQL */ `
        query ListTopicGroups(
          $filter: ModelTopicGroupFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
              id
              color
              sortKey
              name {
                language
                text
              }
              topics {
                items {
                  id
                  name {
                    language
                    text
                  }
                  sortKey
                  topicGroupTopicsId
                }
              }
            }
            nextToken
            __typename
          }
        }
      `
        });

        // Filter only the allowed topic groups
        const filteredTopicGroups = topicGroupsWithTopics.data.listTopicGroups.items
          .filter(group => ALLOWED_TOPIC_GROUP_IDS.includes(group.id))
          .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));

        // Flatten topics from filtered groups
        const topicsFlat = filteredTopicGroups
          .map((topicGroup) => topicGroup.topics.items)
          .flat()
          .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));

        return {
          topicGroups: filteredTopicGroups,
          topics: topicsFlat,
        }

    } catch (error) {
        console.log("error on fetching topics", error);
        captureError(error, {
          feature: 'lecture-management',
          operation: 'fetch-topics',
          tags: { component: 'AdminEditLecture' }
        });
    }
}

  useEffect(() => {
    const fetchData = async () => {
    const [lectureData, topicsData] = await Promise.all([getLectureData(), getTopics()]);
    if (isHDLectureManager(user) && !hasHDIndustry(lectureData?.event)) {
      navigate("/", { replace: true });
      return;
    }
    setLecture(lectureData);
    setLoadedSlug(lectureData?.slug ?? null);
    setCompany(lectureData.company);
    setTopics(topicsData.topics);
    setTopicGroups(topicsData.topicGroups);
    // Show lecture form after data is loaded
    if(lectureData) {
      setShowLectureForm(true);
    }
    };
    fetchData();
  }, []);

  // Select Admin Topic
  const [selectAdminTopicModal, setSelectAdminTopicModal] = useState(false);
  const toggleSelectAdminTopicModal = () => setSelectAdminTopicModal(!selectAdminTopicModal);

  // Save only admin fields
  const updateAdminFields = async () => {
    try {
      setIsSaving(true);
      let lectureDataForLecture = {
        type: lecture?.type,
        companyLecturesId: companyid,
        adminComment: lecture?.adminComment || "",
        status: lecture?.status || "created",
        lectureAdminTopicId: lecture?.adminTopic || null,
        // "slug" only for Admin/Moderator and only when changed vs. the loaded slug; slugHistory is never sent
        ...slugMutationInput(lecture?.slug, { canEditSlug, isUpdate: true, loadedSlug }),
        ...(lecture?.companyBooth && {companyBooth: lecture?.companyBooth}),
        ...(lecture?.dateTimes && {dateTimes: lecture?.dateTimes}),
      };
      if(lecture?.lengthInSeconds) lectureDataForLecture.lengthInSeconds = lecture?.lengthInSeconds;
      if(lecture.hasOwnProperty("approved")) {lectureDataForLecture.approved = lecture?.approved} else {lectureDataForLecture.approved = null};
      if(lecture.hasOwnProperty("approvedInsight")) {lectureDataForLecture.approvedInsight = lecture?.approvedInsight} else {lectureDataForLecture.approvedInsight = null};
      console.log("lectureDataForLecture", lecture)
      const updatedLecture = await client.graphql({
        variables: { input: lectureid ? {...lectureDataForLecture, id: lectureid} :lectureDataForLecture },
        query: 
        /* GraphQL */ `
        mutation UpdateLecture(
          $input: UpdateLectureInput!
          $condition: ModelLectureConditionInput
        ) {
            updateLecture(input: $input, condition: $condition) {
              title {
                language
                text
            }
            speakers {
                items {
                    employee {
                        image {
                        fileName
                        alt
                        }
                    }
                }
            }
            subjects {
                id
                name {
                    language
                    text
                }
            } 
            topics {
                items {
                  topic {
                    id
                    name {
                      language
                      text
                    }
                  }
                }
            }        
            content {
                language
                text
            }
            conditions {
                name
                accepted
            }
            image {
                fileName
                alt
            }
            pdfDe {
                url
                name   
            }
            pdfEn {
                url
                name   
            }
            videoS3Key {
              s3Path
            }
            approvedInsight
            company {
                id
                name
                image {
                    main {
                        fileName
                        alt
                    }
                }
            }
            conditions {
                name
                accepted
            }
            id
            session
            dateTimes
            companyBooth
            room
            type
            presentationLanguage
            createdAt
            updatedAt
            companyLecturesId
            eventLecturesId
            lectureMainSpeakerId
            approved
            status
            __typename
            }
          }
        `
      });
      setIsSaving(false);
    } catch (error) {
      console.warn("Error updating admin fields", error);
      captureError(error, {
        feature: 'lecture-management',
        operation: 'update-admin-fields',
        context: { lectureId: lectureid, lectureData: lecture },
        tags: { component: 'AdminEditLecture' }
      });
      setIsSaving(false);
    }
  };

  // Handle save from LectureRegistration component
  const handleLectureSave = async (lectureData) => {
    try {
      console.log("Lecture saved from form:", lectureData);
      // The lecture form handles its own saving, we just need to reload the data
      const newLectureData = await getLectureData();
      setLecture(newLectureData);
      setLoadedSlug(newLectureData?.slug ?? null);
    } catch (error) {
      console.error("Error handling lecture save:", error);
      captureError(error, {
        feature: 'lecture-management',
        operation: 'handle-lecture-save',
        context: { lectureData },
        tags: { component: 'AdminEditLecture' }
      });
    }
  };


 

  // Handlers
  const handleChange = (e, additionalInstructions) => {
    console.log("change", e.target.attributes.datapath.value, e.target.checked, e.target.value);
    console.log("additionalInstructions", additionalInstructions);
    console.log(e)
    
    // Get values from input
    const datapath = e.target.attributes.datapath.value;
    let value;

    // Check if the input is a checkbox
    if (e.target.type === 'checkbox') {
        value = e.target.checked; // Use 'checked' for checkboxes
    } else {
        value = e.target.value; // Use 'value' for other input types
    }

    // Update current company object
    const newLecture = deepUpdateObject(lecture, datapath, value, additionalInstructions);
    console.log("new lecture", JSON.stringify(newLecture));
    setLecture(newLecture);
  };

  // Combined save function: saves both admin fields and lecture content
  const handleSaveAll = async () => {
    try {
      setIsSaving(true);

      // First save the admin fields
      await updateAdminFields();

      // Then save the lecture content via the LectureRegistration component
      if (lectureRegistrationRef.current?.saveLecture) {
        await lectureRegistrationRef.current.saveLecture();
      }

      // Reload the lecture data after saving
      const newLectureData = await getLectureData();
      setLecture(newLectureData);
      setLoadedSlug(newLectureData?.slug ?? null);

      setIsSaving(false);
    } catch (error) {
      console.error("Error saving lecture and admin fields:", error);
      captureError(error, {
        feature: 'lecture-management',
        operation: 'save-all',
        context: { lectureId: lectureid },
        tags: { component: 'AdminEditLecture' }
      });
      setIsSaving(false);
    }
  }

  
  // Sessions 
  const [newSession, setNewSession] = useState("");


  // Back to top button
  let mybutton;
  window.onscroll = function () {
    mybutton = document.getElementById("btn-back-to-top");
    if (mybutton) scrollFunction(mybutton);
  };

  function scrollFunction(mybutton) {
    if (
      document.body.scrollTop > 20 ||
      document.documentElement.scrollTop > 20
    ) {
      mybutton.style.display = "block";
    } else {
      mybutton.style.display = "none";
    }
  }
  function backToTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

return (
<>
<MDBBtn
  onClick={backToTop}
  id='btn-back-to-top'
  style={{
    position: "fixed",
    bottom: "20px",
    right: "20px",
    display: "none",
    zIndex: "99",
  }}
  className='btn-floating'
  color='primary'
  size='lg'>
  <MDBIcon fas icon="arrow-up" />
</MDBBtn>
    <MDBCard>
      <MDBCardBody>
        <MDBRow>
          <MDBCol size="12" lg="6" xl="4">
            <MDBTypography tag="h5">{t("Event")}</MDBTypography>
            <MDBTypography tag="h6">Lounges 2024 - {lecture.type}</MDBTypography>
            <br></br>
            <MDBTypography tag="h5">{t("Company")}</MDBTypography>
            {company &&
            <>
              {company?.image?.main?.fileName ?
                  <div className='d-flex align-items-center'>
                      {company.image?.main?.fileName ?
                          <img
                              src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/"+company.image.main.fileName}
                              className="rounded-circle border"
                              style={{ height: "50px", width: "50px", objectFit: "contain" }}
                              alt={company.image.alt}
                          />
                      :
                          <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="company-alt" />
                                  
                      }
                      <div className='ms-3'>
                      <Link to={`/admin/company/${company.id}`}>
                      <p className='fw-bold mb-1 text-body'>{company.name}</p>
                      </Link>
                      {/* <p className='text-muted mb-0'><a href={company.website} target="_blank" rel="noreferrer">{company.website}</a></p> */}
                      </div>
                  </div>
                  :
                  <div className='d-flex align-items-center'>
                    <MDBIcon
                        className="rounded-circle border"
                        style={{
                            height: "50px",
                            width: "50px",
                            textAlign: "center",
                            paddingTop: "12px",
                            fontSize: "20px",
                        }}
                        fas
                        icon="user-alt"
                    />
                    <div className='ms-3'>
                      <p className='fw-bold mb-1'>{company.name}</p>
                      <p className='text-muted mb-0'><a href={company.website} target="_blank" rel="noreferrer">{company.website}</a></p>
                    </div>
                  </div>
              }
            </>
            }
            <MDBTypography tag="h5" className="mt-2 mt-xl-3">{t("Company Admins")}</MDBTypography>
            {lecture?.company?.adminEmployees?.items?.length > 0 && lecture?.company?.adminEmployees?.items.map((admin, index) => (
              <div className='d-flex align-items-center'>
              {admin.image?.fileName ?
                          <img
                              src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/"+ admin.image.fileName}
                              className="rounded-circle border"
                              style={{ height: "50px", width: "50px", objectFit: "contain" }}
                              alt={admin.image.alt}
                          />
                          :
                          <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="user-alt" />
                          
              }
                <div className='ms-3'>
                <Link to={`/admin/user/${admin.user.id}`}>
                <p className='fw-bold mb-1 text-body'>{admin.user.title ? admin.user.title + " " : ""}{admin.user.firstName} {admin.user.middleName} {admin.user.lastName}{admin.status === "pending" && <MDBChip className="bg-warning text-white bg-opacity-75 inline" style={{maxWidth: "fit-content"}}>{t("Pending")}</MDBChip>}</p>
                </Link>
                <p className='text-muted mb-0'>{admin.user.contact?.email}</p>
                <p className='text-muted mb-0'>{admin.user.contact?.phone}</p>
                </div>
              </div>
            ))}
            
          </MDBCol>
          <MDBCol size="12" lg="6" xl="4">
            <MDBTypography tag="h5">{t("Erstellung & Update")}</MDBTypography>
            <MDBTypography tag="h6">{t("Erstellt")}: {new Date(lecture?.createdAt).toLocaleString("de-de")}</MDBTypography>
            <MDBTypography tag="h6">{t("Zuletzt aktualisiert")}: {new Date(lecture?.updatedAt).toLocaleString("de-de")}</MDBTypography>
            <MDBTypography tag="h5">{t("Comment")}</MDBTypography>
            <MDBTextArea datapath="adminComment" value={lecture?.adminComment || ""} onChange={handleChange} />
            <MDBTypography tag="h5" className="mt-3">{t("Sessions")}</MDBTypography>
            {lecture?.dateTimes?.length > 0 && lecture.dateTimes.map((dateTime, index) => (
              <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6" key={index}>{new Date(dateTime).toLocaleString("de-de")}</MDBTypography>
              <MDBBtn size="sm" color="danger" onClick={() => setLecture({...lecture, dateTimes: lecture.dateTimes.filter((date, i) => i !== index)})}>{t("Remove")}</MDBBtn>
              </div>
              
            ))}
            <div className='d-flex justify-content-between mt-2'>
            <MDBTypography tag="h6">{t("Add Date")}</MDBTypography>: 
            <input
              type="datetime-local"
              value={newSession || (lecture?.event?.startDate ? lecture.event.startDate + "T08:00" : "")}
              onChange={(e) => setNewSession(e.target.value)}
              />
            <MDBBtn color="primary" onClick={() => setLecture({...lecture, dateTimes: [...(lecture.dateTimes || []), moment.tz(newSession, "Europe/Berlin").toISOString()]})}>{t("Add")}</MDBBtn>
            </div>
            <Link to={`/admin/promotiontimes`} target="_blank" rel="noopener noreferrer">
              <MDBBtn color="primary" size="sm" className="mt-3">{t("Promotions Calendar")}</MDBBtn>
            </Link>
          </MDBCol>
          <MDBCol size="12" lg="6" xl="4">
          {/* Company Booth */}
          <MDBRow>
            <MDBCol size="6">
             <MDBTypography tag="h6">{t("Company Booth")}</MDBTypography>
            </MDBCol>
            <MDBCol size="6">
              <MDBInput 
                  label={t("Company Booth")}
                  size="sm"
                  datapath="companyBooth"
                  value={lecture?.companyBooth || ""}
                  onChange={e => handleChange({...e, target: {value: e.target.value,attributes: {datapath: {value: "companyBooth"}}}} )}
                  />
            </MDBCol>
          </MDBRow>
              
         
           {/* Type */}
           <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Type")}</MDBTypography>
              <MDBSelect
                size="sm"
                data={[
                  { text: t("All"), value: null },
                  { text: t("Marketing"), value: "marketing" },
                  { text: t("Technical"), value: "technical" },
                  { text: t("Educational"), value: "educational" },
                  { text: t("Recruiting"), value: "recruiting" },
                  { text: t("VIP"), value: "vip" },
                  { text: t("Promotion Marketing"), value: "promotion_marketing" },
                  { text: t("Promotion Technical"), value: "promotion_technical" },
                  { text: t("Wien"), value: "lecture_wien" },
                ]}
                datapath="status"
                value={lecture?.type}
                onChange={e => handleChange({...e, target: {value: e?.value,attributes: {datapath: {value: "type"}}}} )}
              />
            </div>
            {/* Topic */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Topic")}</MDBTypography>
              {lecture?.adminTopic && (
                <MDBChip closeIcon onDelete={() => setLecture({...lecture, adminTopic: null})} className="m-0" bgColor="grey" textColor="white">{topics?.find(topic => topic.id === lecture.adminTopic)?.name?.find(name => name.language === i18n.language)?.text}</MDBChip>
              )
              }
              <MDBBtn size="sm" className="py-0" style={{height: "30px"}} onClick={() => toggleSelectAdminTopicModal()} color="primary" outline>
                {t("Select Topic")}
              </MDBBtn>
            </div>
            {/* Length */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Length in seconds")}</MDBTypography>
              <MDBSelect
                size="sm"
                data={[
                  { text: "30 " + t("Minutes"), value: 30 },
                  { text: "45 " + t("Minutes"), value: 45 },
                  { text: "60 " + t("Minutes"), value: 60 },
                ]}
                datapath="lengthInSeconds"
                value={lecture?.lengthInSeconds}
                onChange={e => handleChange({...e, target: {value: parseInt(e?.value),attributes: {datapath: {value: "lengthInSeconds"}}}} )}
              />
            </div>
            {/* Status */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Status")}</MDBTypography>
              <MDBSelect
                size="sm"
                data={[
                  { text: t("Created"), value: "created" },
                  { text: t("Approved"), value: "checked" },
                  { text: t("Not Approved"), value: "not_approved" },
                  { text: t("In Planning"), value: "in_planning" },
                  { text: t("Committed"), value: "committed" },
                ]}
                datapath="status"
                value={lecture?.status}
                onChange={e => handleChange({...e, target: {value: e.value,attributes: {datapath: {value: "status"}}}} )}
              />
            </div>
            {/* Approved */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Approved")}</MDBTypography>
              <div>

                  <MDBCheckbox
                    name='approved'
                    id='approved_yes'
                    label={t("Yes")}
                    inline
                    checked={lecture?.approved === true}
                    onChange={(e) => handleChange({...e, target: {checked: lecture?.approved === true ? null : true, value: lecture?.approved === true ? null : true, attributes: {datapath: {value: "approved"}}}})}
                  />

                  <MDBCheckbox
                    name='approved'
                    id='approved_no'
                    label={t("No")}
                    inline
                    checked={lecture?.approved === false}
                    onChange={(e) => handleChange({...e, target: {checked: lecture?.approved === false ? null : false, value: lecture?.approved === false ? null : false, attributes: {datapath: {value: "approved"}}}})}
                  />
              </div>
            </div>
            {/* Approved Insight */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Approved Insight")}</MDBTypography>
              <div>
                
                  <MDBCheckbox
                    name='approvedInsight'
                    id='approvedInsight_yes'
                    label={t("Yes")}
                    inline
                    checked={lecture?.approvedInsight === true}
                    onChange={(e) => handleChange({...e, target: {checked: lecture?.approvedInsight === true ? null : true, value: lecture?.approvedInsight === true ? null : true, attributes: {datapath: {value: "approvedInsight"}}}})}
                  />

                  <MDBCheckbox
                    name='approvedInsight'
                    id='approvedInsight_no'
                    label={t("No")}
                    inline
                    checked={lecture?.approvedInsight === false}
                    onChange={(e) => handleChange({...e, target: {checked: lecture?.approvedInsight === false ? null : false, value: lecture?.approvedInsight === false ? null : false, attributes: {datapath: {value: "approvedInsight"}}}})}
                  />
              </div>
            </div>
            {/* Public URL slug (Admin/Moderator only) */}
            {canEditSlug && (
              <div className='mt-3'>
                <MDBTypography tag="h6">{t("URL slug")}</MDBTypography>
                <SlugField
                  entity="lecture"
                  entityId={lectureid}
                  value={lecture?.slug}
                  history={lecture?.slugHistory}
                  sourceText={pickSourceText(lecture?.title)}
                  disabled={isSaving}
                  onChange={(slug) => setLecture(oldLecture => ({...oldLecture, slug}))}
                />
              </div>
            )}


          </MDBCol>
        </MDBRow>
        {
          isSaving ? (
            <MDBSpinner color="primary" className="float-end mt-3" />
          )
          :
          <div className="float-end mt-3">
          <MDBBtn color="primary" onClick={() => handleSaveAll()}>{t("Save")}</MDBBtn>
          <br></br>
          <MDBBtn color="link" onClick={() => navigate(-1)}>{t("Back")}</MDBBtn>
          </div>


        }
        <br></br>
        
      </MDBCardBody>
    </MDBCard>
    <MDBCard className="mt-2 mt-xl-4">
      <MDBCardBody>
        <MDBCardTitle>{t("Lecture Content")}</MDBCardTitle>
        {showLectureForm && lecture?.id ? (
          <LectureRegistration
            ref={lectureRegistrationRef}
            companyId={companyid}
            eventConfig={lounges26LectureConfig}
            eventId={lecture?.event?.id || "cb7a916e-ed71-483a-8177-22532ab0b237"}
            lectureId={lectureid}
            onSaveLecture={handleLectureSave}
            onCloseRequest={() => {}}
            isModal={false}
          />
        ) : (
          <div className="text-center p-5">
            <MDBSpinner role="status" />
            <p className="mt-3">{t("Loading lecture data...")}</p>
          </div>
        )}
      </MDBCardBody>
    </MDBCard>

<MDBModal open={selectAdminTopicModal} onClose={() => setSelectAdminTopicModal(false)} staticBackdrop>
  <MDBModalDialog centered size="xl">
      <MDBModalContent>
          <MDBModalHeader>
              <MDBModalTitle>{t("Select Topic")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleSelectAdminTopicModal}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
              <MDBRow className="my-3 my-lg-4">
                {topicGroups?.map((topicGroup) => (
                  <MDBCol size="12" lg="6" key={topicGroup.id} className="mb-4">
                    <div className="mb-3">
                      <h6 className="fw-bold">
                        {topicGroup.name?.find(n => n.language === i18n.language)?.text || topicGroup.name?.[0]?.text}
                      </h6>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {topicGroup.topics?.items?.map((topic) => {
                        const isSelected = lecture?.adminTopic === topic.id;
                        return (
                          <MDBChip
                            key={topic.id}
                            className="cursor-pointer m-0"
                            onClick={() => {
                              if (isSelected) {
                                // Deselect if already selected
                                setLecture(oldLecture => ({...oldLecture, adminTopic: null}));
                              } else {
                                // Select new topic
                                setLecture(oldLecture => ({...oldLecture, adminTopic: topic.id}));
                              }
                            }}
                            style={{
                              cursor: 'pointer',
                              justifyContent: 'flex-start',
                              backgroundColor: isSelected ? "#3dd378" : "#ececec",
                              color: isSelected ? "white" : "black"
                            }}
                          >
                            {topic.name?.find(n => n.language === i18n.language)?.text || topic.name?.[0]?.text}
                          </MDBChip>
                        );
                      })}
                    </div>
                  </MDBCol>
                ))}
              </MDBRow>
          </MDBModalBody>
          <MDBModalFooter>
              <MDBBtn color="secondary" onClick={toggleSelectAdminTopicModal}>{t("Close")}</MDBBtn>
          </MDBModalFooter>
      </MDBModalContent>
  </MDBModalDialog>
</MDBModal>
</>
  )
}
